import { useEffect, useMemo, useRef, useState } from 'react';

const PANEL_WIDTH = 360;
const PANEL_GAP = 16;
const VIEWPORT_MARGIN = 16;
const MOBILE_BREAKPOINT = 720;
const MAX_MESSAGE_CHARACTERS = 2000;

function clamp(value, minimum, maximum) {
  return Math.min(Math.max(value, minimum), Math.max(minimum, maximum));
}

export function getDiscussionPanelLayout(anchorRect, viewport) {
  const width = viewport.width || 0;
  const height = viewport.height || 0;
  if (width < MOBILE_BREAKPOINT) {
    return {
      position: 'fixed',
      left: 0,
      right: 0,
      bottom: 0,
      width: '100%',
      maxHeight: '78vh',
      borderRadius: '18px 18px 0 0',
    };
  }

  const anchor = anchorRect || {
    left: width - PANEL_WIDTH - VIEWPORT_MARGIN,
    right: width - PANEL_WIDTH - VIEWPORT_MARGIN,
    top: VIEWPORT_MARGIN,
    bottom: VIEWPORT_MARGIN,
  };
  const fitsRight = anchor.right + PANEL_GAP + PANEL_WIDTH <= width - VIEWPORT_MARGIN;
  const left = fitsRight
    ? anchor.right + PANEL_GAP
    : anchor.left - PANEL_GAP - PANEL_WIDTH;
  const maxHeight = Math.max(320, Math.min(620, height - (VIEWPORT_MARGIN * 2)));
  return {
    position: 'fixed',
    left: clamp(left, VIEWPORT_MARGIN, width - PANEL_WIDTH - VIEWPORT_MARGIN),
    top: clamp(anchor.top, VIEWPORT_MARGIN, height - maxHeight - VIEWPORT_MARGIN),
    width: PANEL_WIDTH,
    maxHeight,
    borderRadius: 18,
  };
}

function initials(label) {
  const parts = String(label || 'Mindmappi collaborator')
    .trim()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
  if (parts.length === 0) return 'M';
  return `${parts[0][0] || ''}${parts[1]?.[0] || ''}`.toUpperCase();
}

function authorColor(userId) {
  let hash = 0;
  for (const character of String(userId || 'former-collaborator')) {
    hash = ((hash << 5) - hash) + character.charCodeAt(0);
    hash |= 0;
  }
  return `hsl(${Math.abs(hash) % 360} 62% 38%)`;
}

function formatTimestamp(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function displayError(error) {
  if (error?.code === 'node_discussion_rate_limit_reached') {
    const wait = error.retryAfterSeconds
      ? ` Try again in about ${error.retryAfterSeconds} seconds.`
      : ' Please try again shortly.';
    return `You’ve sent messages a little too quickly.${wait}`;
  }
  return error?.message || 'This discussion could not be loaded.';
}

function NodeDiscussionPanel({
  node,
  anchorRect,
  isDarkMode,
  currentUserId,
  thread,
  isLoading,
  isLoadingOlder,
  isSending,
  error,
  onClose,
  onLoadOlder,
  onSend,
  onRefresh,
}) {
  const [draft, setDraft] = useState('');
  const [viewport, setViewport] = useState(() => ({
    width: typeof window === 'undefined' ? 1024 : window.innerWidth,
    height: typeof window === 'undefined' ? 768 : window.innerHeight,
  }));
  const composerRef = useRef(null);
  const messagesRef = useRef(null);
  const messageCount = thread?.messages?.length || 0;
  const layout = useMemo(
    () => getDiscussionPanelLayout(anchorRect, viewport),
    [anchorRect, viewport]
  );

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleResize = () => setViewport({
      width: window.innerWidth,
      height: window.innerHeight,
    });
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('keydown', handleKeyDown);
    composerRef.current?.focus({ preventScroll: true });
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  useEffect(() => {
    const element = messagesRef.current;
    if (element && !isLoadingOlder) {
      element.scrollTop = element.scrollHeight;
    }
  }, [isLoadingOlder, messageCount]);

  const palette = isDarkMode
    ? {
        panel: '#111827',
        raised: '#1f2937',
        border: '#374151',
        text: '#f9fafb',
        muted: '#9ca3af',
        accent: '#60a5fa',
        input: '#0f172a',
      }
    : {
        panel: '#ffffff',
        raised: '#f8fafc',
        border: '#dbe2ea',
        text: '#111827',
        muted: '#64748b',
        accent: '#2563eb',
        input: '#ffffff',
      };

  const submit = async () => {
    if (!draft.trim() || draft.length > MAX_MESSAGE_CHARACTERS || isSending) return;
    const sent = await onSend(draft);
    if (sent) {
      setDraft('');
      composerRef.current?.focus({ preventScroll: true });
    }
  };

  return (
    <section
      role="dialog"
      aria-modal="false"
      aria-labelledby="node-discussion-title"
      className="node-discussion-panel"
      data-export-ignore="true"
      style={{
        ...layout,
        zIndex: 180,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxSizing: 'border-box',
        background: palette.panel,
        color: palette.text,
        border: `1px solid ${palette.border}`,
        boxShadow: '0 22px 60px rgba(15, 23, 42, 0.34)',
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
      onDoubleClick={(event) => event.stopPropagation()}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
          padding: '16px 16px 12px',
          borderBottom: `1px solid ${palette.border}`,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              color: palette.accent,
              fontSize: 12,
              fontWeight: 750,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            Node discussion
          </div>
          <h2
            id="node-discussion-title"
            style={{
              margin: '4px 0 0',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: 18,
            }}
          >
            {(node?.text || '').trim() || 'Untitled node'}
          </h2>
        </div>
        <button
          type="button"
          aria-label="Close node discussion"
          onClick={onClose}
          style={{
            width: 36,
            height: 36,
            flex: '0 0 auto',
            border: `1px solid ${palette.border}`,
            borderRadius: 999,
            background: 'transparent',
            color: palette.text,
            cursor: 'pointer',
            fontSize: 22,
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </header>

      <div
        ref={messagesRef}
        className="node-discussion-messages"
        aria-live="polite"
        style={{
          flex: '1 1 auto',
          minHeight: 180,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          padding: 16,
        }}
      >
        {thread?.page?.hasMore && (
          <button
            type="button"
            onClick={onLoadOlder}
            disabled={isLoadingOlder}
            style={{
              display: 'block',
              margin: '0 auto 14px',
              border: `1px solid ${palette.border}`,
              borderRadius: 999,
              padding: '7px 12px',
              background: palette.raised,
              color: palette.text,
              cursor: isLoadingOlder ? 'wait' : 'pointer',
            }}
          >
            {isLoadingOlder ? 'Loading…' : 'Load earlier messages'}
          </button>
        )}

        {isLoading && <p role="status">Opening discussion…</p>}

        {!isLoading && !error && messageCount === 0 && (
          <div style={{ padding: '28px 12px', textAlign: 'center' }}>
            <div aria-hidden="true" style={{ fontSize: 28 }}>💬</div>
            <p style={{ margin: '10px 0 4px', fontWeight: 700 }}>
              Start the conversation
            </p>
            <p style={{ margin: 0, color: palette.muted, fontSize: 13, lineHeight: 1.5 }}>
              Keep thoughts and decisions close to this idea.
            </p>
          </div>
        )}

        {error && (
          <div role="alert" style={{ color: '#ef4444', lineHeight: 1.45 }}>
            <p>{displayError(error)}</p>
            <button type="button" onClick={onRefresh}>Try again</button>
          </div>
        )}

        {(thread?.messages || []).map((message) => {
          const label = message.author?.displayName || 'Mindmappi collaborator';
          const isCurrentUser = message.author?.userId === currentUserId;
          return (
            <article
              key={message.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '34px minmax(0, 1fr)',
                gap: 10,
                marginBottom: 16,
              }}
            >
              <div
                aria-hidden="true"
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 999,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: authorColor(message.author?.userId),
                  color: '#fff',
                  fontSize: 11,
                  fontWeight: 800,
                }}
              >
                {initials(label)}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: 13 }}>
                    {isCurrentUser ? 'You' : label}
                  </strong>
                  <time
                    dateTime={message.createdAt}
                    style={{ color: palette.muted, fontSize: 11 }}
                  >
                    {formatTimestamp(message.createdAt)}
                  </time>
                </div>
                <p
                  style={{
                    margin: '4px 0 0',
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'anywhere',
                    lineHeight: 1.5,
                    fontSize: 14,
                  }}
                >
                  {message.body}
                </p>
              </div>
            </article>
          );
        })}
      </div>

      <footer
        style={{
          padding: 14,
          borderTop: `1px solid ${palette.border}`,
          background: palette.raised,
        }}
      >
        <label htmlFor="node-discussion-composer" style={{ position: 'absolute', left: -10000 }}>
          Add to this discussion
        </label>
        <textarea
          id="node-discussion-composer"
          ref={composerRef}
          value={draft}
          maxLength={MAX_MESSAGE_CHARACTERS}
          rows={3}
          placeholder="Add to this discussion…"
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              void submit();
            }
          }}
          style={{
            width: '100%',
            resize: 'vertical',
            minHeight: 70,
            maxHeight: 150,
            boxSizing: 'border-box',
            border: `1px solid ${palette.border}`,
            borderRadius: 12,
            padding: 10,
            background: palette.input,
            color: palette.text,
            font: 'inherit',
          }}
        />
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            marginTop: 8,
          }}
        >
          <span style={{ color: palette.muted, fontSize: 11 }}>
            {draft.length}/{MAX_MESSAGE_CHARACTERS} · Ctrl/⌘ + Enter
          </span>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={!draft.trim() || isSending}
            style={{
              border: 'none',
              borderRadius: 999,
              padding: '9px 15px',
              background: palette.accent,
              color: '#fff',
              cursor: !draft.trim() || isSending ? 'not-allowed' : 'pointer',
              opacity: !draft.trim() || isSending ? 0.55 : 1,
              fontWeight: 750,
            }}
          >
            {isSending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </footer>
    </section>
  );
}

export default NodeDiscussionPanel;
